import { CircularProgress, createStyles, makeStyles } from '@material-ui/core';
import React, { useState } from 'react';

import {
  PublishStatus,
  TaskStatus,
  TaskType,
  useCreateTaskMutation,
  useGetPlexDetailsQuery,
} from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { PlexName } from '../../common/PlexName';
import { DialogActions } from '../../dialog/DialogActions';
import { DialogTopBar } from '../../dialog/DialogTopBar';
import { CreateOrEditDialogTitle } from '../../dialog/post/CreateOrEditDialogTitle';
import { useDialogState } from '../../dialog/useDialogState';
import { SplitButtonMenu } from '../../shared/SplitButtonMenu';
import { usePlexRoute } from '../usePlexRoute';
import { PlexTaskTitleView } from './PlexTaskTitle';
import { TaskBodyView } from './TaskBody';

type Props = {
  type: TaskType;
  plexId: string;
};

type State = {
  title: string;
  body: SlateEditorValue;
};

export const CreateTaskDialog = ({ plexId, type }: Props): React.ReactElement => {
  const classes = useStyles();
  const { data: plexData } = useGetPlexDetailsQuery({ variables: { plexId } });
  const [, updatePlexRoute] = usePlexRoute();
  const [, setDialogState] = useDialogState();
  const [state, setState] = useState<State>({ title: '', body: SlateEditorValue.new() });
  const [createTask, { loading }] = useCreateTaskMutation();

  if (!plexData?.loggedIn?.plexById) {
    return <CircularProgress size={24} />;
  }

  const onCreateClick = (publishStatus: PublishStatus) => () => {
    createTask({
      variables: {
        task: {
          plexId,
          type,
          publishStatus,
          status: TaskStatus.Open,
          title: state.title,
          body: state.body.value,
        },
      },
      update: (cache) => {
        cache.evict({ fieldName: 'tasks' });
      },
    }).then(({ data }) => {
      if (data != null) {
        setDialogState(null, 'replace');
        updatePlexRoute({ selectedItem: { type: 'task', permalink: data.createTask.permalink } }, 'replace');
      }
    });
  };

  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexName
          name={plexData.loggedIn.plexById.name}
          color={plexData.loggedIn.plexById.color}
          className={classes.plexName}
        />
      </DialogTopBar>

      <CreateOrEditDialogTitle />

      <div className={classes.content}>
        <PlexTaskTitleView
          mode="edit"
          value={state.title}
          onChange={(value: string) => {
            setState((state) => ({ ...state, title: value }));
          }}
          saving={loading}
        />

        <TaskBodyView
          mode="edit"
          value={state.body}
          initialEntities={{ expanded_link: [], mention: [] }}
          onChange={(value) => {
            setState((state) => ({ ...state, body: value }));
          }}
          saving={loading}
        />
      </div>

      <DialogActions
        cta={
          <SplitButtonMenu
            disabled={loading}
            color="primary"
            variant="contained"
            options={[
              {
                onClick: onCreateClick(PublishStatus.Internal),
                label: 'Create',
              },
              {
                onClick: onCreateClick(PublishStatus.Published),
                label: 'Create and Publish',
              },
              {
                onClick: onCreateClick(PublishStatus.Draft),
                label: 'Create draft',
              },
            ]}
          />
        }
      />
    </div>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
    content: {
      padding: spacings.normal,
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
  })
);
